import React, { useState, useEffect, useRef } from "react";

import { Icon, Button } from "@atoms";
import { useAppState } from "@state";
import { AppLink } from "@base";
import useLgcaFooterData from "@staticQueries/lgca/FooterQuery";
import classNames from "classnames";
import useTouchDevice from "@hooks/useTouchDevice";

const LgcaEcFooter = () => {
  const isTouchDevice = useTouchDevice();

  const { social } = useLgcaFooterData();
  const [{ ec }] = useAppState();
  const [pageNum, setPageNum] = useState(ec.nav.pageNum);
  const [chapter, setChapter] = useState(ec.nav.chapter);
  const [colors, setColors] = useState(
    ec.nav.chapters?.[ec.nav.chapter].colors
  );
  const timeout = useRef();
  const [hidePrompt, setHidePrompt] = useState(false);

  useEffect(() => {
    setHidePrompt(false);
    timeout.current = setTimeout(() => {
      setHidePrompt(true);
    }, 7000);
    if (ec.nav.pageNum !== pageNum && ec.nav.pageNum !== 0) {
      setPageNum(
        ec.nav.pageNum -
          (ec.chapters
            .slice(0, ec.nav.chapter)
            .reduce((a, b) => a + (b?.pages || 0), 0) -
            1)
      );
    } else if (!ec.nav.pageNum) {
      setPageNum(0);
    }
    if (ec.nav.chapter !== chapter) {
      setChapter(ec.nav.chapter);
    }
    return () => {
      if (timeout.current) {
        clearTimeout(timeout);
      }
    };
  }, [ec]);

  useEffect(() => {
    setColors(ec.chapters?.[chapter]?.colors);
  }, [chapter]);

  return (
    <div className="fixed bottom-0 left-0 right-0 z-10 hidden h-12 transition-all duration-500 xxs:h-8 sm:block sm:h-16">
      {!!pageNum && !!chapter && chapter !== -1 && (
        <div className="absolute top-0 left-0 right-0 h-1 w-full -translate-y-full transform">
          <div
            style={{
              background: colors?.accent,
              width: `${
                ((pageNum - 1) / ((ec?.chapters[chapter]?.pages || 0) - 1)) *
                100
              }%`,
            }}
            className={classNames("absolute top-0 bottom-0 left-0", {
              "transition-all duration-500":
                pageNum !== 1 && pageNum !== ec?.chapters[chapter]?.pages,
            })}
          >
            <div className="jusitfy-center absolute right-0 top-0 bottom-0 -mr-6 flex items-center">
              <svg
                className="h-6 w-6"
                viewBox="0 0 32 32"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle
                  cx="16"
                  cy="16"
                  r="10"
                  fill={colors?.primary || "#ffffffff"}
                  id="sunCircle"
                />
                <g>
                  <path
                    id="sunOutline"
                    d="M16.0027 0C7.16613 0 0 7.16613 0 16.0027C0 24.8393 7.16613 32.0054 16.0027 32.0054C24.8393 32.0054 32.0054 24.8393 32.0054 16.0027C32.0054 7.16613 24.8393 0 16.0027 0ZM30.0956 10.8716C30.4112 11.7368 30.5635 12.7216 30.5635 12.7216L25.6881 12.4768L29.2685 9.15763C29.2631 9.15763 29.7582 9.95205 30.0956 10.8716ZM27.4892 6.36082C28.0823 7.06819 28.5611 7.93879 28.5611 7.93879L23.8925 9.38072L26.1234 5.03316C26.1234 5.03316 26.8635 5.60993 27.4892 6.36082ZM21.7596 2.23091C21.7596 2.23091 22.6519 2.5193 23.5008 3.00901C24.3006 3.47152 25.0461 4.12447 25.0461 4.12447L21.1556 7.07363L21.7596 2.23091ZM18.6036 1.22972C19.5123 1.38752 20.4373 1.75208 20.4373 1.75208L17.7875 5.85479L16.6992 1.09369C16.7046 1.09369 17.6405 1.06104 18.6036 1.22972ZM13.3964 1.22972C14.305 1.07193 15.2954 1.09369 15.2954 1.09369L14.2071 5.85479L11.5572 1.75208C11.5627 1.75208 12.4333 1.3984 13.3964 1.22972ZM8.49923 3.01445C9.2991 2.55195 10.2404 2.23635 10.2404 2.23635L10.8499 7.07907L6.95936 4.12991C6.95936 4.12991 7.65584 3.49872 8.49923 3.01445ZM4.5108 6.36082C5.10389 5.65346 5.87655 5.03316 5.87655 5.03316L8.10746 9.38072L3.43887 7.93879C3.43887 7.93879 3.88505 7.11172 4.5108 6.36082ZM1.90444 10.8716C2.22003 10.0065 2.73695 9.15763 2.73695 9.15763L6.31729 12.4768L1.44193 12.7216C1.44193 12.7216 1.57252 11.7912 1.90444 10.8716ZM1.00119 16.0027C1.00119 15.0831 1.19708 14.1092 1.19708 14.1092L5.69699 16.0027L1.19708 17.8963C1.19708 17.8963 1.00119 16.9821 1.00119 16.0027ZM1.90444 21.1284C1.58885 20.2632 1.43649 19.2784 1.43649 19.2784L6.31185 19.5232L2.73151 22.8424C2.73695 22.8478 2.2418 22.048 1.90444 21.1284ZM4.5108 25.6446C3.9177 24.9373 3.43887 24.0667 3.43887 24.0667L8.10746 22.6247L5.87655 26.9723C5.87655 26.9723 5.14198 26.3901 4.5108 25.6446ZM10.2404 29.7691C10.2404 29.7691 9.34807 29.4807 8.49923 28.991C7.69937 28.5285 6.95392 27.8755 6.95392 27.8755L10.8444 24.9264L10.2404 29.7691ZM13.3964 30.7703C12.4877 30.6125 11.5627 30.2479 11.5627 30.2479L14.2125 26.1452L15.3008 30.9063C15.2954 30.9117 14.3595 30.9444 13.3964 30.7703ZM6.73627 16.0027C6.73627 10.8825 10.8825 6.73627 16.0027 6.73627C21.1229 6.73627 25.2692 10.8825 25.2692 16.0027C25.2692 21.1229 21.1229 25.2692 16.0027 25.2692C10.8825 25.2692 6.73627 21.1175 6.73627 16.0027ZM18.6036 30.7703C17.6949 30.9281 16.7046 30.9063 16.7046 30.9063L17.7929 26.1452L20.4428 30.2479C20.4373 30.2534 19.5667 30.6016 18.6036 30.7703ZM23.5008 28.991C22.7009 29.4535 21.7596 29.7691 21.7596 29.7691L21.1501 24.9264L25.0406 27.8755C25.0461 27.8755 24.3496 28.5013 23.5008 28.991ZM27.4892 25.6446C26.8961 26.352 26.1234 26.9723 26.1234 26.9723L23.8925 22.6247L28.5611 24.0667C28.5611 24.0667 28.1204 24.8937 27.4892 25.6446ZM30.0956 21.1284C29.78 21.9935 29.2631 22.8424 29.2631 22.8424L25.6827 19.5232L30.5581 19.2784C30.5635 19.2838 30.4275 20.2088 30.0956 21.1284ZM26.303 16.0027L30.8029 14.1092C30.8029 14.1092 30.9988 15.0287 30.9988 16.0027C30.9988 16.9223 30.8029 17.8963 30.8029 17.8963L26.303 16.0027Z"
                    fill={colors?.secondary || "#ffffffff"}
                  />
                </g>
              </svg>
            </div>
          </div>
        </div>
      )}
      <div className="flex h-full items-center justify-between bg-white p-3">
        {(!chapter || chapter === -1) && (
          <div className="-ml-2 flex items-center">
            {social?.map(p => {
              return (
                <div key={p.url} className="px-2">
                  <Button
                    rounded
                    size="xxs"
                    to={p.url}
                    target="_blank"
                    color="orange"
                    // onClick={e => handleClick(e, "Twitter")}
                  >
                    <Icon
                      name={p.platform}
                      className="h-4 w-4 xxs:h-3 xxs:w-3"
                      fitHeight
                    />
                  </Button>
                </div>
              );
            })}
          </div>
        )}
        {!!pageNum && !!chapter && chapter !== -1 && (
          <div
            className="font-serif -mx-2 flex items-center text-xl"
            style={{ color: colors?.primary }}
          >
            <ul className="flex">
              {ec.chapters
                .filter((c, i) => i !== 0)
                .map((c, i) => {
                  return (
                    <li key={c.uri} className="flex items-center">
                      <AppLink
                        to={`/${c.uri}`}
                        style={
                          chapter === i + 1
                            ? { borderColor: colors?.secondary }
                            : null
                        }
                        className={classNames(
                          "mx-1 mt-px flex h-6 w-6 items-center justify-center rounded-full",
                          { "border-2": chapter === i + 1 }
                        )}
                      >
                        {c.number}
                      </AppLink>
                      {c.number === chapter && (
                        <>
                          <span
                            className="mx-2 hidden w-6 p-px sm:inline-flex"
                            style={{ background: colors?.primary }}
                          />
                          <span className="font-extended mx-2 hidden text-base leading-none sm:inline-flex">
                            {c.title}
                          </span>
                        </>
                      )}
                    </li>
                  );
                })}
            </ul>
            {/* <span className="mx-2 mt-px">1</span>
            
              How did we get here?
            <span className="mx-2 mt-px">2</span>{" "}
            <span className="mx-2 mt-px">3</span>
            <span className="mx-2 mt-px">4</span>{" "}
            <span className="mx-2 mt-px">5</span> */}
          </div>
        )}
        <div className="font-extended items-center text-sm font-bold uppercase text-blue sm:flex md:m-0">
          {/* {((!pageNum && !chapter) || chapter === -1) && (
              <div className="mr-3 md:hidden">
                <Icon name="lgcaSun" className="h-8 w-8" />
              </div>
            )} */}
          {(!pageNum || ec?.nav?.pageNum === ec?.totalPages) && (
            <span
              className={classNames("hidden sm:block", {})}
              style={
                pageNum && ec?.nav?.pageNum !== ec?.totalPages
                  ? { color: ec?.colors?.accent }
                  : null
              }
            >
              Climate. Community. Collaboration.
            </span>
          )}
          {!!pageNum && ec?.nav?.pageNum !== ec?.totalPages && (
            <span
              style={pageNum ? { color: ec?.colors?.accent } : null}
              className={classNames("ml-auto flex items-center", {
                "animate-scroll": !isTouchDevice,
                "opacity-0": hidePrompt,
                // "animate-swipe": isTouchDevice,
              })}
            >
              {isTouchDevice && (
                <Icon
                  name="lgcaArrow"
                  fitHeight
                  className={classNames(
                    "-mt-px h-6 w-6 rotate-180 transform",
                    {}
                  )}
                />
              )}
              {isTouchDevice ? "Swipe" : "Scroll"}
              <Icon
                name="lgcaArrow"
                fitHeight
                className={classNames("h-6 w-6", {
                  "ml-1 rotate-90 transform": !isTouchDevice,
                  "-mt-px": isTouchDevice,
                })}
              />
            </span>
          )}
          {((!pageNum && !chapter) || chapter === -1) && (
            <div className="ml-3 hidden md:flex">
              <Icon name="lgcaSun" className="h-8 w-8" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

LgcaEcFooter.defaultProps = {};

export default LgcaEcFooter;
